// MYKOKU Forms Auto-fill open flow (Parent Web).
//
// When a logged-in parent opens a MYKOKU Forms link, mint a short-lived
// Auto-fill Pass from the backend (using the app's own session) and open the
// public form with it so the parent's details are pre-filled. The pass is
// never the session token; it is bound to the one form and expires quickly.
// If minting fails (or the user isn't a parent), the form still opens
// anonymously — autofill is a progressive enhancement, never a gate.

import api from "../api/api";

const FORMS_PATH_PATTERN = /^\/(f|forms)\/([A-Za-z0-9_-]+)$/;

export const getFormsWebBaseUrl = (): string =>
  String(process.env.REACT_APP_FORMS_WEB_URL || "").replace(/\/+$/, "");

// Returns the form public code when `url` is a MYKOKU Forms link on the
// configured domain, else null.
export const getMykokuFormsCode = (url: string): string | null => {
  const base = getFormsWebBaseUrl();
  if (!base || !url) return null;
  try {
    const target = new URL(url);
    if (target.origin !== new URL(base).origin) return null;
    const match = target.pathname.match(FORMS_PATH_PATTERN);
    return match ? match[2] : null;
  } catch {
    return null;
  }
};

export const isMykokuFormsUrl = (url: string): boolean =>
  getMykokuFormsCode(url) !== null;

// Strict charset (no quotes/brackets) so the injected anchor cannot break the
// surrounding parsed HTML; leading-char guard avoids URLs already in an attr.
const FORMS_URL_CANDIDATE_PATTERN =
  /https?:\/\/[A-Za-z0-9.-]+(?::[0-9]+)?\/(?:f|forms)\/[A-Za-z0-9_-]+/g;

// Wraps bare MYKOKU Forms URLs in notification text with anchors carrying a
// data-mykoku-forms marker, so the renderer can attach the mint-and-open
// handler. Non-Forms URLs are left untouched.
export const linkifyMykokuFormsUrls = (html: string): string =>
  html.replace(
    new RegExp(`(^|[^"'=>])(${FORMS_URL_CANDIDATE_PATTERN.source})`, "g"),
    (match, prefix: string, url: string) =>
      isMykokuFormsUrl(url)
        ? `${prefix}<a href="${url}" data-mykoku-forms="1" class="text-blue-600 underline break-all">${url}</a>`
        : match,
  );

// Tells the forms web app that the app-vs-browser choice is already settled,
// so it renders the form instead of bouncing the visitor back here via its
// automatic app handoff. Without it a visitor who scanned inside Parent Web
// lands on <parent>/open/<code> — an extra round trip that either reopens this
// app or shows its "Continue in browser" page, when they were already looking
// at the form. forms-web strips the parameter on arrival.
const APP_HANDOFF_PARAM = "mykoku_app_handoff";
const APP_HANDOFF_VALUE = "1";

const buildFormUrl = (publicCode: string, autofillPass?: string): string => {
  const base = getFormsWebBaseUrl();
  const params = new URLSearchParams();
  if (autofillPass) params.set("ap", autofillPass);
  params.set(APP_HANDOFF_PARAM, APP_HANDOFF_VALUE);
  return `${base}/f/${encodeURIComponent(publicCode)}?${params.toString()}`;
};

// Autofill is a progressive enhancement, never a gate, so the mint must never
// hold the visitor on a spinner. The shared axios instance sets no global
// timeout, so without this the request can hang until the browser gives up —
// and because the caller awaits it before navigating, the form would not open
// at all. On timeout we fall through to the anonymous form.
const MINT_TIMEOUT_MS = 4000;

const mintAutofillPass = async (publicCode: string): Promise<string> => {
  const response = await api.post(
    "/mykoku-forms/autofill-pass",
    {
      public_code: publicCode,
      channel: "web",
    },
    { timeout: MINT_TIMEOUT_MS },
  );
  const data = response?.data?.data ?? response?.data ?? {};
  return String(data.autofill_pass || "");
};

// Opens a MYKOKU Forms link with autofill when possible. Always resolves —
// falls back to the plain (anonymous) form on any failure. Returns true when
// the form was opened with an Auto-fill Pass.
export const openMykokuFormWithAutofill = async (
  url: string,
  open: (targetUrl: string) => void = (targetUrl) =>
    window.open(targetUrl, "_blank", "noopener,noreferrer"),
): Promise<boolean> => {
  const publicCode = getMykokuFormsCode(url);
  if (!publicCode) {
    open(url);
    return false;
  }

  try {
    const autofillPass = await mintAutofillPass(publicCode);
    if (autofillPass) {
      open(buildFormUrl(publicCode, autofillPass));
      return true;
    }
  } catch {
    // Minting is best-effort; fall through to the anonymous form.
  }

  open(buildFormUrl(publicCode));
  return false;
};
